/**
 * The battle model (BUILDLIST 62.17, `ARCHITECTURE.md` §5.7b): a sector battle stepped from its
 * conditions, so length and outcome both come out of the stepping and nothing is decided at the
 * start. Step 1 of the build: the state, the step, inputs, the end conditions, the outcome. Pure and
 * deterministic (keyed draws). Not wired into the sector yet; the constants are lane B's first
 * calibration (`sweeps/2026-10-09-surrogate-calibration.md`) and get fitted in step 2.
 *
 * Per side: its units (health, alive), how many are **engaged** and how many still **closing**, its
 * **policy** (aggression, the share it will lose before it withdraws, sneak or assault), its
 * **tactics** (the defence it gets from cover, a garrison, structures) and its **mission** (take or
 * hold, read through the objective's progress). Each step (one second):
 *   1. contact: closing units engage at a rate set by the closing distance, aggression and stance;
 *   2. attrition: each side's engaged units take damage at `KILL_RATE` per opposing engaged shooter,
 *      × the shooters' effectiveness ÷ the targets' defence — Lanchester's square law, which lane B's
 *      odds follow — with a keyed draw on each step's volley, focused on the weakest engaged unit;
 *   3. mission: the objective's progress moves toward the side with more engaged strength there;
 *   4. end: elimination, the mission met, a side past its withdrawal threshold, or the time limit.
 */
import { withdrawAtOf } from './doctrine';
import type { Doctrine, BattleOutcome, BattleSetup, FactionId, UnitId } from './types';
import type { GameState } from '../types';
import { getPlayerWorldPosition } from '../core/gameState';
import { vecLength, vecSub } from '../math';
import { personSetOf } from '../core/scaleProfile';
import type { SkillLevel } from '../ai/bot/tuning';
import { SeededRandom } from '../core/random';
import { FITTED_PARAMS } from './battleModelParams';

/**
 * The model's constants, as data (62.17 step 2): fitted against full battles by
 * `npm run sim:model-fit` and stamped with the `BATTLE_VERSION` they were fitted under. A model
 * whose battle version is stale still runs; the fit is re-run when the version moves.
 */
export interface ModelParams {
  /** Kills per engaged shooter per second. */
  killRate: number;
  /** Seconds for an aggressive (1.0) assault to close from its landing to contact, on a mixed-size planet. */
  closeSeconds: number;
  /** Seconds of uncontested strength for an objective to go from even to taken. */
  objectiveSeconds: number;
  /** The holder's defence: in a garrison, in the open; and per structure. */
  defenceGarrison: number;
  defenceOpen: number;
  defencePerStructure: number;
  /** A withdrawing side's extra losses: this share of one volley from everyone engaged against it. */
  pursuitShare: number;
  /** A battle's time limit (s). */
  timeLimit: number;
  /** The BATTLE_VERSION the constants were fitted under (0 = not fitted). */
  battleVersion: number;
  /** Seconds per step (default 1, the surrogate tier). Long battles may step coarser until the closed-form tier exists (§5.7b). */
  stepSeconds?: number;
}

/** Lane B's first numbers, before any fit: the reference for the fit's "before" column. */
export const UNFITTED_PARAMS: ModelParams = {
  killRate: 5 / 60, closeSeconds: 20, objectiveSeconds: 45,
  defenceGarrison: 1.25, defenceOpen: 1.1, defencePerStructure: 0.1,
  pursuitShare: 0.5, timeLimit: 300, battleVersion: 0,
};

/** The params a battle runs with by default: the latest fit (`battleModelParams.ts`), else the unfitted numbers. */
export const MODEL_PARAMS: ModelParams = FITTED_PARAMS ?? UNFITTED_PARAMS;

/** Kept for readers of step 1: the unfitted kill rate. */
export const KILL_RATE = UNFITTED_PARAMS.killRate;

const SKILL_EFFECT: Record<string, number> = { easy: 0.7, medium: 0.85, hard: 1, expert: 1.15 };
const SIZE_DISTANCE: Record<string, number> = { small: 0.7, mixed: 1, large: 1.3, megaplanet: 2 };

export interface ModelUnit { unitId: UnitId; health: number; alive: boolean; engaged: boolean }

export interface ModelPolicy {
  /** 0–1: how fast it closes and how hard it presses. */
  aggression: number;
  /** Share of its starting health it will lose before it disengages (1 = fights to the last). */
  withdrawAt: number;
  stance: 'assault' | 'sneak';
}

export interface ModelSide {
  faction: FactionId;
  units: ModelUnit[];
  policy: ModelPolicy;
  /** × damage dealt (skill, kit). */
  effect: number;
  /** ÷ damage taken (cover, a garrison, structures held). */
  defence: number;
  mission: 'take' | 'hold';
  /** Health it started with (the withdrawal threshold's reference; arrivals add to it). */
  startHealth: number;
  withdrawn: boolean;
}

export interface ModelBattle {
  id: string;
  seed: string;
  /** Seconds since the battle began. */
  t: number;
  sides: [ModelSide, ModelSide];
  /** −1 … 1: +1 = side 0's mission met at the objective, −1 = side 1's. */
  objective: number;
  /** Seconds a closing unit needs, at aggression 1. */
  closeSeconds: number;
  timeLimit: number;
  /** Whether the battle has an objective to take (a deathmatch has none: only elimination, withdrawal or time end it). */
  hasObjective: boolean;
  params: ModelParams;
  ended: { winner: 0 | 1 | null; reason: string } | null;
}

/** Inputs during the span (R11's envelope, on the running record while no Match exists). */
export type ModelInput =
  | { kind: 'arrive'; side: 0 | 1; units: Array<{ unitId: UnitId; health: number }> }
  | { kind: 'withdraw'; side: 0 | 1 }
  | { kind: 'policy'; side: 0 | 1; policy: Partial<ModelPolicy> }
  /** The cross-tier seam (§5.7 item 6): shells from outside land on `side` — `shells` × `lethality` × 100 damage, on the weakest first. */
  | { kind: 'fire'; side: 0 | 1; shells: number; lethality: number };

/** Units closer than this many person-lengths to a living enemy count as engaged when a full battle is summarised. */
export const CONTACT_LENGTHS = 16;

/**
 * Full → model (§5.7 mapping 1; decision 111: leaving a full battle hands it to the surrogate). The
 * battle's setup gives the sides; the match state gives who still stands, at what health, and who is
 * in contact (a living enemy within CONTACT_LENGTHS); `seconds` is how far the battle has run. The
 * objective leans to whoever leads on it.
 */
export function modelFromMatch(setup: BattleSetup, state: GameState, seconds: number, params: ModelParams = MODEL_PARAMS, objectiveLeader: 0 | 1 | null = null): ModelBattle {
  const m = modelFromSetup(setup, params);
  const contact = CONTACT_LENGTHS * personSetOf(state).k;
  const byTeam = setup.sides.slice(0, 2).map((_, t) => state.players.filter(p => p.teamIndex === t).sort((a, b) => a.id - b.id));
  const livingEnemies = (t: number) => state.players.filter(p => p.alive && p.teamIndex !== t);
  m.sides.forEach((side, i) => {
    const players = byTeam[i];
    const enemies = livingEnemies(i);
    side.units = side.units.map((u, k) => {
      const p = players[k];
      if (!p) return { ...u, alive: false, health: 0, engaged: false };
      if (!p.alive) return { ...u, alive: false, health: 0, engaged: false };
      const at = getPlayerWorldPosition(p, state.planets);
      const engaged = enemies.some(e => vecLength(vecSub(getPlayerWorldPosition(e, state.planets), at)) <= contact);
      return { ...u, alive: true, health: Math.max(1, p.health), engaged };
    });
  });
  m.t = seconds;
  if (m.hasObjective && objectiveLeader !== null) m.objective = objectiveLeader === 0 ? 0.5 : -0.5;
  return m;
}

function policyFrom(doctrine: Doctrine, stance: 'assault' | 'sneak'): ModelPolicy {
  const personality = doctrine.personality;
  const aggression = personality === 'aggressive' ? 1 : personality === 'cautious' || personality === 'defensive' ? 0.6 : 0.8;
  // 56.7: the doctrine's commitment; unset, today's reading of the round cap (a short cap leaves early)
  const withdrawAt = withdrawAtOf(doctrine);
  return { aggression, withdrawAt, stance };
}

/** A model battle from a sector battle's setup. Side 0 is the holder (as in `buildBattleSetup`). */
export function modelFromSetup(setup: BattleSetup, params: ModelParams = MODEL_PARAMS): ModelBattle {
  const holder = setup.opening.defender;
  const sides = setup.sides.slice(0, 2).map((side, i): ModelSide => {
    const isHolder = side.faction === holder;
    const stance: 'assault' | 'sneak' = side.faction === setup.opening.attacker && setup.opening.attackerStance === 'sneak' ? 'sneak' : 'assault';
    const units = side.units.map(u => ({ unitId: u.unitId, health: u.health, alive: u.health > 0, engaged: false }));
    const defence = isHolder ? (setup.opening.garrisoned ? params.defenceGarrison : params.defenceOpen) + params.defencePerStructure * setup.structures.length : 1;
    return {
      faction: side.faction, units, policy: policyFrom(side.doctrine, stance),
      effect: SKILL_EFFECT[side.doctrine.skill as SkillLevel] ?? 1, defence,
      mission: isHolder || (holder === null && i === 0) ? 'hold' : 'take',
      startHealth: units.reduce((a, u) => a + u.health, 0), withdrawn: false,
    };
  }) as [ModelSide, ModelSide];
  return {
    id: setup.id, seed: setup.seed, t: 0, sides, objective: 0,
    closeSeconds: params.closeSeconds * (SIZE_DISTANCE[setup.map.planetSize] ?? 1),
    timeLimit: params.timeLimit, ended: null,
    hasObjective: setup.mode !== 'deathmatch', params,
  };
}

const alive = (s: ModelSide) => s.units.filter(u => u.alive);
const engaged = (s: ModelSide) => s.units.filter(u => u.alive && u.engaged);
const healthOf = (s: ModelSide) => s.units.reduce((a, u) => a + (u.alive ? u.health : 0), 0);

function draw(b: ModelBattle, purpose: string): number {
  return new SeededRandom(`${b.seed}-${b.id}-model-${b.t}-${purpose}`).next();
}

/** Apply an input at the current time. */
export function applyInput(b0: ModelBattle, input: ModelInput): ModelBattle {
  if (b0.ended) return b0;
  const b = clone(b0);
  const side = b.sides[input.side];
  if (input.kind === 'arrive') {
    for (const u of input.units) side.units.push({ unitId: u.unitId, health: u.health, alive: u.health > 0, engaged: false });
    side.startHealth += input.units.reduce((a, u) => a + u.health, 0);
  } else if (input.kind === 'withdraw') {
    withdraw(b, input.side, 'withdrawn by order');
  } else if (input.kind === 'fire') {
    takeDamage(side, input.shells * input.lethality * 100, true);
    const a0 = alive(b.sides[0]).length, a1 = alive(b.sides[1]).length;
    if (!a0 || !a1) b.ended = { winner: a0 ? 0 : a1 ? 1 : null, reason: 'elimination' };
  } else {
    side.policy = { ...side.policy, ...input.policy };
  }
  return b;
}

function clone(b: ModelBattle): ModelBattle {
  return { ...b, sides: b.sides.map(s => ({ ...s, policy: { ...s.policy }, units: s.units.map(u => ({ ...u })) })) as [ModelSide, ModelSide] };
}

/** A side disengages: pursuit losses from everyone engaged against it, and the other side holds the field. */
function withdraw(b: ModelBattle, i: 0 | 1, reason: string): void {
  const me = b.sides[i], them = b.sides[1 - i];
  const volley = engaged(them).length * b.params.killRate * 100 * them.effect / me.defence * b.params.pursuitShare;
  takeDamage(me, volley, true);
  me.withdrawn = true;
  for (const u of me.units) u.engaged = false;
  b.ended = { winner: alive(them).length ? (1 - i) as 0 | 1 : null, reason };
}

/** Damage lands on the weakest engaged unit first (focused fire), then the next. */
function takeDamage(side: ModelSide, amount: number, anyone = false): void {
  let left = amount;
  while (left > 1e-9) {
    const pool = (anyone ? alive(side) : engaged(side)).sort((a, b) => a.health - b.health || (a.unitId < b.unitId ? -1 : a.unitId > b.unitId ? 1 : 0));
    if (!pool.length) return;
    const u = pool[0];
    const hit = Math.min(u.health, left);
    u.health -= hit; left -= hit;
    if (u.health <= 1e-9) { u.health = 0; u.alive = false; u.engaged = false; }
  }
}

/** One second of battle. */
export function step(b0: ModelBattle, dt = 1): ModelBattle {
  if (b0.ended) return b0;
  const b = clone(b0);
  b.t += dt;
  // 1. contact
  b.sides.forEach((s, i) => {
    const sneak = s.policy.stance === 'sneak' ? 0.5 : 1;
    const rate = dt * s.policy.aggression * sneak / b.closeSeconds;
    s.units.forEach((u, k) => { if (u.alive && !u.engaged && draw(b, `contact-${i}-${k}`) < rate) u.engaged = true; });
  });
  // the holder engages whoever comes (it is already there); a side with nobody engaged against it cannot be hurt
  const e0 = engaged(b.sides[0]).length, e1 = engaged(b.sides[1]).length;
  if (e0 + e1 > 0 && (e0 === 0 || e1 === 0)) {
    // only one side has closed: the other's nearest unit meets it
    const waiting = e0 === 0 ? b.sides[0] : b.sides[1];
    const first = alive(waiting).find(u => !u.engaged);
    if (first && waiting.mission === 'hold') first.engaged = true;
  }
  // 2. attrition (both volleys from the state at the start of the step)
  const v = b.sides.map((s, i) => {
    const them = b.sides[1 - i];
    const shooters = engaged(them).length;
    if (!shooters || !engaged(s).length) return 0;
    const noise = 0.5 + draw(b, `volley-${i}`);   // 0.5–1.5
    return shooters * b.params.killRate * 100 * dt * them.effect / s.defence * noise;
  });
  takeDamage(b.sides[0], v[0]);
  takeDamage(b.sides[1], v[1]);
  // 3. mission
  const s0 = engaged(b.sides[0]).reduce((a, u) => a + u.health, 0) * b.sides[0].effect;
  const s1 = engaged(b.sides[1]).reduce((a, u) => a + u.health, 0) * b.sides[1].effect;
  if (b.hasObjective && s0 + s1 > 0) b.objective = Math.max(-1, Math.min(1, b.objective + (s0 - s1) / (s0 + s1) * dt / b.params.objectiveSeconds));
  // 4. end conditions
  const a0 = alive(b.sides[0]).length, a1 = alive(b.sides[1]).length;
  if (!a0 || !a1) { b.ended = { winner: a0 ? 0 : a1 ? 1 : null, reason: 'elimination' }; return b; }
  for (const i of [0, 1] as const) {
    const s = b.sides[i];
    if (s.policy.withdrawAt < 1 && healthOf(s) <= s.startHealth * (1 - s.policy.withdrawAt)) { withdraw(b, i, 'withdrew'); return b; }
  }
  const taker = b.sides.findIndex(s => s.mission === 'take');
  if (taker >= 0 && (taker === 0 ? b.objective >= 1 : b.objective <= -1)) { b.ended = { winner: taker as 0 | 1, reason: 'objective' }; return b; }
  if (b.t >= b.timeLimit) {
    // time: the holder keeps it unless the taker leads on the objective
    const lead = b.objective > 0 ? 0 : b.objective < 0 ? 1 : null;
    const holderIdx = b.sides.findIndex(s => s.mission === 'hold');
    b.ended = { winner: lead !== null && b.sides[lead].mission === 'take' ? lead : holderIdx >= 0 ? holderIdx as 0 | 1 : null, reason: 'time' };
  }
  return b;
}

/** Step until `t` (or the end), applying `inputs` at their times. */
export function runTo(b0: ModelBattle, t: number, inputs: Array<{ t: number; input: ModelInput }> = []): ModelBattle {
  let b = b0;
  const queue = [...inputs].sort((x, y) => x.t - y.t);
  let q = 0;
  const dt = b0.params.stepSeconds ?? 1;
  while (!b.ended && b.t < t - 1e-9) {
    while (q < queue.length && queue[q].t <= b.t + 1e-9) b = applyInput(b, queue[q++].input);
    b = step(b, dt);
  }
  return b;
}

/** Run to the end (the time limit bounds it). */
export function runToEnd(b0: ModelBattle, inputs: Array<{ t: number; input: ModelInput }> = []): ModelBattle {
  return runTo(b0, b0.timeLimit + 1, inputs);
}

/** The battle's result as the sector reads it. Its length is seconds (an action-clock length, R7). */
export function toOutcome(b: ModelBattle, setup: BattleSetup): BattleOutcome {
  const units: BattleOutcome['units'] = b.sides.flatMap(s => s.units.map(u => ({
    unitId: u.unitId, alive: u.alive, health: u.alive ? Math.max(1, Math.round(u.health)) : 0, kills: 0, shots: 0, hits: 0,
  })));
  const winner = b.ended?.winner ?? null;
  return {
    battleId: setup.id, winner: winner === null ? null : b.sides[winner].faction, reason: b.ended?.reason ?? null,
    rounds: 0, clock: 'action', seconds: b.t, units, planets: setup.planets ?? [],
    scars: { craterVertices: 0, massLostFraction: 0 }, shots: 0, hits: 0, highlightShots: 0,
    wentLoud: !(b.sides.some(s => s.policy.stance === 'sneak') && b.ended?.reason === 'time' && b.sides.every(s => !s.units.some(u => !u.alive))),
    objectiveLeader: b.objective > 0 ? b.sides[0].faction : b.objective < 0 ? b.sides[1].faction : null,
    structuresSurvived: setup.structures.map(() => true),
  };
}
